// src/components/BookCover/NewVersionDialog.jsx
// Dialog for creating a new cover version.
// Allows author to set: name, collaborators, criteria, cover type.

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Avatar,
  ToggleButtonGroup, ToggleButton, IconButton, Chip,
  FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Divider,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon from '@mui/icons-material/Close';
import BrushIcon from '@mui/icons-material/Brush';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { API_BASE } from '../../utils/api';

const COVER_TYPES = [
  { id: 'softcover', label: 'Softcover (Paperback)' },
  { id: 'softcover_flaps', label: 'Softcover with Flaps' },
  { id: 'hardcover', label: 'Hardcover' },
  { id: 'hardcover_jacket', label: 'Hardcover with Dust Jacket' },
  { id: 'ebook', label: 'Ebook' },
];

export default function NewVersionDialog({ open, onClose, onConfirm, nextVersionNumber, purchasedVersions = [] }) {
  const [name, setName] = useState('');
  const [criteria, setCriteria] = useState('');
  const [coverType, setCoverType] = useState('');
  const [sourceVersionId, setSourceVersionId] = useState('');
  const [collaborators, setCollaborators] = useState([]); // [{userId, username, role}]

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // Role popup for searched user
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingRole, setPendingRole] = useState('design');

  const [creating, setCreating] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName(`Version ${nextVersionNumber || ''}`);
      setCriteria('');
      setCoverType('');
      setSourceVersionId('');
      setCollaborators([]);
      setSearchQuery('');
      setSearchResults([]);
      setShowSearch(false);
      setPendingUser(null);
    }
  }, [open, nextVersionNumber]);

  // Debounced user search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const token = localStorage.getItem('rv_access_token');
        const res = await fetch(`${API_BASE}/api/users/search?q=${encodeURIComponent(searchQuery)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const existingIds = new Set(collaborators.map(c => c.userId));
          const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
          const currentUserId = currentUser.id || currentUser._id;
          const filtered = (data.users || data || [])
            .filter(u => !existingIds.has(u._id || u.id))
            .filter(u => (u._id || u.id) !== currentUserId); // Exclude self
          setSearchResults(filtered);
        }
      } catch { /* ignore */ }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery, collaborators]);

  const handleUserSelect = (user) => {
    setPendingUser(user);
    setPendingRole('design');
    setSearchResults([]);
    setSearchQuery('');
  };

  const handleConfirmCollaborator = () => {
    if (!pendingUser) return;
    setCollaborators(prev => [...prev, {
      userId: pendingUser._id || pendingUser.id,
      username: pendingUser.username,
      role: pendingRole,
    }]);
    setPendingUser(null);
    setShowSearch(false);
  };

  const handleRemoveCollaborator = (userId) => {
    setCollaborators(prev => prev.filter(c => c.userId !== userId));
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await onConfirm({
        name: name.trim() || undefined,
        collaborators: collaborators.map(c => ({ userId: c.userId, role: c.role })),
        criteria: criteria.trim() || undefined,
        coverType: coverType || undefined,
        sourceVersionId: sourceVersionId || undefined,
      });
      onClose();
    } catch {
      // Error handled by parent
    } finally {
      setCreating(false);
    }
  };

  const getAvatarLetter = (n) => (n || 'U')[0].toUpperCase();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Create New Version
      </DialogTitle>

      <DialogContent dividers>
        {/* Version Name */}
        <TextField
          fullWidth
          label="Version Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          sx={{ mb: 3 }}
        />

        {/* Cover Type */}
        <FormControl fullWidth size="small" sx={{ mb: 3 }}>
          <InputLabel>Cover Type (optional)</InputLabel>
          <Select
            value={coverType}
            onChange={(e) => setCoverType(e.target.value)}
            label="Cover Type (optional)"
          >
            <MenuItem value="">
              <em>Not specified</em>
            </MenuItem>
            {COVER_TYPES.map((ct) => (
              <MenuItem key={ct.id} value={ct.id}>{ct.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ mb: 2 }} />

        {/* Start from Purchased Design */}
        {purchasedVersions.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#3B3478', fontWeight: 600 }}>
              📦 Start from Purchased Design (optional)
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <InputLabel>Choose a purchased cover</InputLabel>
              <Select
                value={sourceVersionId}
                onChange={(e) => setSourceVersionId(e.target.value)}
                label="Choose a purchased cover"
              >
                <MenuItem value="">
                  <em>Blank canvas</em>
                </MenuItem>
                {purchasedVersions.map((pv) => (
                  <MenuItem key={pv.id} value={pv.id}>
                    📦 {pv.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Design Criteria */}
        <Typography variant="subtitle2" sx={{ mb: 1, color: '#3B3478', fontWeight: 600 }}>
          📋 Design Criteria (optional)
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder={"Describe your vision for the cover...\n\n• Color scheme & mood\n• Style (minimalist, illustrated, photographic)\n• Must-include elements\n• Reference covers you like"}
          value={criteria}
          onChange={(e) => setCriteria(e.target.value)}
          inputProps={{ maxLength: 2000 }}
          helperText={`${criteria.length} / 2000`}
          sx={{ mb: 3 }}
        />

        <Divider sx={{ mb: 2 }} />

        {/* Collaborators */}
        <Typography variant="subtitle2" sx={{ mb: 1, color: '#3B3478', fontWeight: 600 }}>
          👥 Collaborators (optional)
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Add users who can help design or review this version
        </Typography>

        {/* Added collaborators */}
        {collaborators.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {collaborators.map((c) => (
              <Chip
                key={c.userId}
                avatar={<Avatar sx={{ bgcolor: c.role === 'design' ? '#8b5cf6' : '#0ea5e9' }}>
                  {getAvatarLetter(c.username)}
                </Avatar>}
                label={`${c.username} (${c.role === 'design' ? '🎨 Designer' : '👁️ Viewer'})`}
                onDelete={() => handleRemoveCollaborator(c.userId)}
                variant="outlined"
                sx={{
                  borderColor: c.role === 'design' ? '#c4b5fd' : '#7dd3fc',
                  backgroundColor: c.role === 'design' ? '#ede9fe' : '#e0f2fe',
                }}
              />
            ))}
          </Box>
        )}

        {/* Pending user role selection */}
        {pendingUser && (
          <Box sx={{
            p: 2, mb: 2,
            borderRadius: 1,
            border: '1px solid #e0e0e0',
            backgroundColor: '#fafafa',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#5A52A3' }}>
                {getAvatarLetter(pendingUser.username)}
              </Avatar>
              <Typography variant="body2" fontWeight={600}>{pendingUser.username}</Typography>
              <Box sx={{ flexGrow: 1 }} />
              <IconButton size="small" onClick={() => setPendingUser(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
              Select role:
            </Typography>

            <ToggleButtonGroup
              value={pendingRole}
              exclusive
              onChange={(_, val) => val && setPendingRole(val)}
              fullWidth
              size="small"
              sx={{ mb: 1.5 }}
            >
              <ToggleButton value="design" sx={{
                '&.Mui-selected': { backgroundColor: '#ede9fe', borderColor: '#8b5cf6', color: '#8b5cf6' },
              }}>
                <BrushIcon sx={{ mr: 0.5, fontSize: 16 }} /> Design
              </ToggleButton>
              <ToggleButton value="view_only" sx={{
                '&.Mui-selected': { backgroundColor: '#e0f2fe', borderColor: '#0ea5e9', color: '#0ea5e9' },
              }}>
                <VisibilityIcon sx={{ mr: 0.5, fontSize: 16 }} /> View Only
              </ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="contained"
              size="small"
              onClick={handleConfirmCollaborator}
              sx={{
                backgroundColor: pendingRole === 'design' ? '#8b5cf6' : '#0ea5e9',
                '&:hover': { filter: 'brightness(0.9)' },
              }}
            >
              Add {pendingRole === 'design' ? 'Designer' : 'Viewer'}
            </Button>
          </Box>
        )}

        {/* Search bar */}
        {!pendingUser && (
          showSearch ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, position: 'relative' }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search by username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searching && <CircularProgress size={16} />}
              <IconButton size="small" onClick={() => { setShowSearch(false); setSearchResults([]); setSearchQuery(''); }}>
                <CloseIcon fontSize="small" />
              </IconButton>

              {searchResults.length > 0 && (
                <Box sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 40,
                  mt: 0.5,
                  bgcolor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  zIndex: 10,
                  maxHeight: 200,
                  overflow: 'auto',
                }}>
                  {searchResults.map((user) => (
                    <Box
                      key={user._id || user.id}
                      onClick={() => handleUserSelect(user)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#f5f5f5' },
                      }}
                    >
                      <Avatar sx={{ width: 28, height: 28, mr: 1, fontSize: '0.875rem' }}>
                        {getAvatarLetter(user.username)}
                      </Avatar>
                      <Typography variant="body2">{user.username}</Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {!searching && searchQuery.trim() && searchResults.length === 0 && (
                <Box sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 40,
                  mt: 0.5,
                  bgcolor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 1,
                  zIndex: 10,
                }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    No users found
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={() => setShowSearch(true)}
              sx={{
                borderColor: '#5A52A3',
                color: '#5A52A3',
                borderRadius: '10px',
              }}
            >
              Search Users
            </Button>
          )
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={creating}>Cancel</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={creating}
          sx={{ backgroundColor: '#3B3478', '&:hover': { backgroundColor: '#2a2558' } }}
        >
          {creating ? 'Creating...' : 'Create Version'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
